import jsPDF from 'jspdf';
import JsBarcode from 'jsbarcode';

/**
 * Generate swing tags PDF
 * Specifications:
 * - Letter size paper (8.5" x 11" = 215.9mm x 279.4mm)
 * - Each tag: 70mm x 40mm
 * - 3 tags per row
 * - Information: name, size, color, date, price
 */

export const generateSwingTagsPDF = async (item) => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });

  // Updated Tag dimensions
  const tagWidth = 98; // 70 * 1.4
  const tagHeight = 40;
  const tagsPerRow = 2;
  const marginLeft = 9.95; // Updated centering for 2 tags
  const marginTop = 10;
  const gapX = 0; 
  const gapY = 5; 

  const variations = item.variations || [];
  const tags = [];

  variations.forEach(variation => {
    for (let i = 0; i < variation.quantity; i++) {
      tags.push({
        name: item.name,
        color: variation.color,
        size: variation.size,
        price: variation.price,
        sku: variation.sku,
        date: new Date().toLocaleDateString()
      });
    }
  });

  if (tags.length === 0) throw new Error('No tags to generate');

  tags.forEach((tag, index) => {
    // 14 tags per page (7 rows * 2)
    const row = Math.floor((index % 14) / tagsPerRow);
    const col = index % tagsPerRow;
    
    if (index > 0 && index % 14 === 0) {
      pdf.addPage();
    }

    const x = marginLeft + col * (tagWidth + gapX);
    const y = marginTop + row * (tagHeight + gapY);

    drawSwingTag(pdf, x, y, tagWidth, tagHeight, tag);
  });

  const fileName = `swing-tags-${item.name.replace(/\s+/g, '-')}-${Date.now()}.pdf`;
  pdf.save(fileName);
};

const drawSwingTag = (pdf, x, y, width, height, tag) => {
  // 1. CROP MARKS (Light gray guides)
  pdf.setDrawColor(200); 
  pdf.setLineWidth(0.1);
  const s = 3; 
  pdf.line(x, y, x + s, y); pdf.line(x, y, x, y + s);
  pdf.line(x + width, y, x + width - s, y); pdf.line(x + width, y, x + width, y + s);
  pdf.line(x, y + height, x + s, y + height); pdf.line(x, y + height, x, y + height - s);
  pdf.line(x + width, y + height, x + width - s, y + height); pdf.line(x + width, y + height, x + width, y + height - s);

  // 2. BRAND NAME (Added "mrich" at the top)
  pdf.setTextColor(100); // Dark gray for the brand
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('mrich.ca', x + 5, y + 5);

  // 3. ITEM INFO (Shifted down slightly to accommodate brand)
  pdf.setTextColor(0); // Back to black
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  const nameLines = pdf.splitTextToSize(tag.name, width / 2);
  pdf.text(nameLines, x + 5, y + 12); // Moved from y+8 to y+12

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  const infoY = y + 12 + (nameLines.length * 5);
  pdf.text(`Color: ${tag.color}`, x + 5, infoY);
  pdf.text(`Size: ${tag.size}`, x + 5, infoY + 5);

  /*
  // 4. BARCODE & SKU
  const barcodeWidth = 45;
  const barcodeHeight = 10;
  const barcodeX = x + (width - barcodeWidth) / 2;
  const barcodeY = y + height - 18;

  if (tag.sku) {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, tag.sku, { format: 'CODE128', width: 1, height: 30, displayValue: false });
    const barcodeImage = canvas.toDataURL('image/png');
    pdf.addImage(barcodeImage, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight);
    
    pdf.setFontSize(6);
    pdf.text(tag.sku, barcodeX + barcodeWidth / 2, barcodeY + barcodeHeight + 3, { align: 'center' });
  }*/

  // 4. UPDATED BARCODE LOGIC
  const barcodeWidth = 60; // Increased from 45 to give it more horizontal room
  const barcodeHeight = 12; // Increased slightly for better vertical scanning
  const barcodeX = x + (width - barcodeWidth) / 2;
  const barcodeY = y + height - 20;

  if (tag.sku) {
    try {
      const canvas = document.createElement('canvas');
      JsBarcode(canvas, tag.sku, {
        format: 'CODE128',
        width: 2,           // INCREASED: Makes the bars thicker and easier to scan
        height: 40,          // INCREASED: Standard height for better sensor pickup
        displayValue: false,
        margin: 10           // ADDED: Creates a "Quiet Zone" so the scanner knows where the barcode starts
      });
      
      const barcodeImage = canvas.toDataURL('image/png');
      // We use 'FAST' compression to keep the bars sharp
      pdf.addImage(barcodeImage, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight, undefined, 'FAST');
      
      pdf.setFontSize(7);
      pdf.text(tag.sku, barcodeX + barcodeWidth / 2, barcodeY + barcodeHeight + 4, { align: 'center' });
    } catch (error) {
      console.error('Barcode error:', error);
    }
  }

  // 5. PRICE & DATE (Standardized to the right side)
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  const priceText = `$${tag.price.toFixed(2)}`;
  pdf.text(priceText, barcodeX + barcodeWidth, barcodeY - 2, { align: 'right' });

  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'normal');
  pdf.text(tag.date, x + width - 5, y + height - 3, { align: 'right' });
  
  // Scissors Icon
  pdf.setFontSize(8);
  pdf.text('✂', x + width - 5, y + 3);
};

/*
const drawSwingTag = (pdf, x, y, width, height, tag) => {
  // Scissors Icon (Now acts as the only cut guide)
  pdf.setDrawColor(0);
  pdf.setFontSize(8);
  pdf.text('✂', x + width - 5, y + 3);

  // Item Information (Left Aligned)
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  const nameLines = pdf.splitTextToSize(tag.name, width / 2);
  pdf.text(nameLines, x + 5, y + 8);

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  const infoY = y + 8 + (nameLines.length * 5);
  pdf.text(`Color: ${tag.color}`, x + 5, infoY);
  pdf.text(`Size: ${tag.size}`, x + 5, infoY + 5);

  // Barcode Logic
  const barcodeWidth = 45;
  const barcodeHeight = 10;
  const barcodeX = x + (width - barcodeWidth) / 2;
  const barcodeY = y + height - 18;

  if (tag.sku) {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, tag.sku, { format: 'CODE128', width: 1, height: 30, displayValue: false });
    const barcodeImage = canvas.toDataURL('image/png');
    pdf.addImage(barcodeImage, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight);
    
    pdf.setFontSize(6);
    pdf.text(tag.sku, barcodeX + barcodeWidth / 2, barcodeY + barcodeHeight + 3, { align: 'center' });
  }

  // Price (Right Aligned above Barcode)
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  const priceText = `$${tag.price.toFixed(2)}`;
  pdf.text(priceText, barcodeX + barcodeWidth, barcodeY - 2, { align: 'right' });

  // Date (Bottom Right)
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'normal');
  pdf.text(tag.date, x + width - 5, y + height - 3, { align: 'right' });
};


const drawSwingTag = (pdf, x, y, width, height, tag) => {
  // Border & Cut Indicator
  pdf.setDrawColor(0);
  pdf.setLineWidth(0.3);
  pdf.rect(x, y, width, height);
  pdf.setFontSize(8);
  pdf.text('✂', x + width - 5, y + 3);

  // Item Information (Left Aligned)
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  const nameLines = pdf.splitTextToSize(tag.name, width / 2); // Use half width for text
  pdf.text(nameLines, x + 5, y + 8);

  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  const infoY = y + 8 + (nameLines.length * 5);
  pdf.text(`Color: ${tag.color}`, x + 5, infoY);
  pdf.text(`Size: ${tag.size}`, x + 5, infoY + 5);

  // Barcode (Centered horizontally)
  const barcodeWidth = 45; // Slightly wider barcode for the wider tag
  const barcodeHeight = 10;
  const barcodeX = x + (width - barcodeWidth) / 2;
  const barcodeY = y + height - 18;

  if (tag.sku) {
    const canvas = document.createElement('canvas');
    JsBarcode(canvas, tag.sku, { format: 'CODE128', width: 1, height: 30, displayValue: false });
    const barcodeImage = canvas.toDataURL('image/png');
    pdf.addImage(barcodeImage, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight);
    
    pdf.setFontSize(6);
    pdf.text(tag.sku, barcodeX + barcodeWidth / 2, barcodeY + barcodeHeight + 3, { align: 'center' });
  }

  // Price (Right Aligned above Barcode)
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  const priceText = `$${tag.price.toFixed(2)}`;
  // Anchored to the right edge of the barcode
  pdf.text(priceText, barcodeX + barcodeWidth, barcodeY - 2, { align: 'right' });

  // Date (Bottom Right)
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'normal');
  pdf.text(tag.date, x + width - 5, y + height - 3, { align: 'right' });
};

export const generateSwingTagsPDF = async (item) => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });

  // Tag dimensions
  const tagWidth = 70;
  const tagHeight = 40;
  const tagsPerRow = 3;
  const marginLeft = 7.95; // Center 3 tags (215.9 - 3*70) / 2
  const marginTop = 10;
  const gapX = 0; // No horizontal gap between tags
  const gapY = 5; // 5mm vertical gap

  // Collect all variations to print
  const variations = item.variations || [];
  const tags = [];

  variations.forEach(variation => {
    for (let i = 0; i < variation.quantity; i++) {
      tags.push({
        name: item.name,
        color: variation.color,
        size: variation.size,
        price: variation.price,
        sku: variation.sku,
        date: new Date().toLocaleDateString()
      });
    }
  });

  if (tags.length === 0) {
    throw new Error('No tags to generate');
  }

  let currentPage = 0;
  
  tags.forEach((tag, index) => {
    const row = Math.floor((index % 21) / tagsPerRow); // 21 tags per page (7 rows * 3)
    const col = index % tagsPerRow;
    
    // Add new page if needed
    if (index > 0 && index % 21 === 0) {
      pdf.addPage();
      currentPage++;
    }

    const x = marginLeft + col * (tagWidth + gapX);
    const y = marginTop + row * (tagHeight + gapY);

    drawSwingTag(pdf, x, y, tagWidth, tagHeight, tag);
  });

  // Save PDF
  const fileName = `swing-tags-${item.name.replace(/\s+/g, '-')}-${Date.now()}.pdf`;
  pdf.save(fileName);
};
*/

/**
 * Draw a single swing tag
 */
/*
const drawSwingTag = (pdf, x, y, width, height, tag) => {
  // Border
  pdf.setDrawColor(0);
  pdf.setLineWidth(0.3);
  pdf.rect(x, y, width, height);

  // Add scissors icon at top (cut line indicator)
  pdf.setFontSize(8);
  pdf.text('✂', x + width - 5, y + 3);

  // Item name - bold and larger
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  const nameLines = pdf.splitTextToSize(tag.name, width - 10);
  pdf.text(nameLines, x + 5, y + 8);

  // Color and Size
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  const colorSizeY = y + 8 + (nameLines.length * 5);
  pdf.text(`Color: ${tag.color}`, x + 5, colorSizeY);
  pdf.text(`Size: ${tag.size}`, x + 5, colorSizeY + 5);

  // Price - large and prominent
  pdf.setFontSize(16);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`$${tag.price.toFixed(2)}`, x + 5, y + height - 10);

  // Date printed - small, bottom right
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'normal');
  pdf.text(tag.date, x + width - 25, y + height - 3);

  // Barcode - center bottom
  if (tag.sku) {
    try {
      const canvas = document.createElement('canvas');
      JsBarcode(canvas, tag.sku, {
        format: 'CODE128',
        width: 1,
        height: 30,
        displayValue: false,
        margin: 0
      });
      
      const barcodeImage = canvas.toDataURL('image/png');
      const barcodeWidth = 40;
      const barcodeHeight = 10;
      const barcodeX = x + (width - barcodeWidth) / 2;
      const barcodeY = y + height - 20;
      
      pdf.addImage(barcodeImage, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight);
      
      // SKU text below barcode
      pdf.setFontSize(6);
      pdf.text(tag.sku, barcodeX + barcodeWidth / 2, barcodeY + barcodeHeight + 3, { align: 'center' });
    } catch (error) {
      console.error('Error generating barcode:', error);
      // Fallback: just show SKU text
      pdf.setFontSize(7);
      pdf.text(`SKU: ${tag.sku}`, x + width / 2, y + height - 15, { align: 'center' });
    }
  }
};


const drawSwingTag = (pdf, x, y, width, height, tag) => {
  // Border & Scissors (Existing logic)
  pdf.setDrawColor(0);
  pdf.setLineWidth(0.3);
  pdf.rect(x, y, width, height);
  pdf.setFontSize(8);
  pdf.text('✂', x + width - 5, y + 3);

  // Item name
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  const nameLines = pdf.splitTextToSize(tag.name, width - 10);
  pdf.text(nameLines, x + 5, y + 8);

  // Color and Size
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  const colorSizeY = y + 8 + (nameLines.length * 5);
  pdf.text(`Color: ${tag.color}`, x + 5, colorSizeY);
  pdf.text(`Size: ${tag.size}`, x + 5, colorSizeY + 5);

  // --- REPOSITIONED ELEMENTS ---

  // 1. Barcode (Anchor point for the price)
  const barcodeWidth = 40;
  const barcodeHeight = 10;
  const barcodeX = x + (width - barcodeWidth) / 2;
  const barcodeY = y + height - 18; // Shifted down slightly to make room above

  if (tag.sku) {
    try {
      const canvas = document.createElement('canvas');
      JsBarcode(canvas, tag.sku, {
        format: 'CODE128',
        width: 1,
        height: 30,
        displayValue: false,
        margin: 0
      });
      const barcodeImage = canvas.toDataURL('image/png');
      pdf.addImage(barcodeImage, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight);
      
      // SKU text below barcode
      pdf.setFontSize(6);
      pdf.text(tag.sku, barcodeX + barcodeWidth / 2, barcodeY + barcodeHeight + 3, { align: 'center' });
    } catch (error) {
      console.error('Barcode error:', error);
    }
  }

  // 2. Price (Moved on top of the barcode, aligned to the right edge of barcode)
  pdf.setFontSize(14); // Slightly smaller to ensure it fits the "top" area better
  pdf.setFont('helvetica', 'bold');
  const priceText = `$${tag.price.toFixed(2)}`;
  // Align to the right edge of the barcode: (barcodeX + barcodeWidth)
  pdf.text(priceText, barcodeX + barcodeWidth, barcodeY - 2, { align: 'right' });

  // 3. Date (Kept at the very bottom right)
  pdf.setFontSize(7);
  pdf.setFont('helvetica', 'normal');
  pdf.text(tag.date, x + width - 5, y + height - 3, { align: 'right' });
};
*/

/**
 * Generate a preview of swing tags (for testing/preview)
 */
export const generateSwingTagsPreview = (item) => {
  // This could return a data URL or blob for preview purposes
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter'
  });

  const variations = item.variations || [];
  const firstVariation = variations[0];

  if (firstVariation) {
    const tag = {
      name: item.name,
      color: firstVariation.color,
      size: firstVariation.size,
      price: firstVariation.price,
      sku: firstVariation.sku,
      date: new Date().toLocaleDateString()
    };

    drawSwingTag(pdf, 7.95, 10, 70, 40, tag);
  }

  return pdf.output('dataurlstring');
};
